import React, { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Download, Mail, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { productsData } from '../data/productsData';
import { groupForSelection } from '../utils/productFamilies';
import { Button } from './ui/button';
import { DownloadCatalogButton } from './DownloadCatalogButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pick the right localized name for a variant.
const localizedName = (variant, language) =>
  language === 'ar' ? variant.nameAr : language === 'en' ? variant.nameEn : variant.nameFr;

// Pick the right localized family label.
const localizedFamily = (family, language) =>
  language === 'ar' ? family.ar : language === 'en' ? family.en : family.fr;

// Replace {tokens} in a translation template.
const fmt = (tpl, vars) =>
  Object.keys(vars).reduce((s, k) => s.replaceAll(`{${k}}`, vars[k]), tpl);

const FamilyAnchorBar = ({ groups, direction }) => {
  const { language, t } = useLanguage();
  return (
    <div className="sticky top-16 z-20 bg-white/85 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div
          className={`flex items-center gap-2 overflow-x-auto no-scrollbar ${
            direction === 'rtl' ? 'flex-row-reverse' : ''
          }`}
        >
          <span className="text-xs uppercase tracking-wider font-semibold text-gray-500 shrink-0 hidden md:inline">
            {t('selection.jumpTo')} :
          </span>
          {groups.map(({ family }) => {
            const Icon = family.icon;
            return (
              <a
                key={family.slug}
                href={`#family-${family.slug}`}
                data-testid={`family-anchor-${family.slug}`}
                className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 hover:border-transparent hover:bg-gradient-to-r ${family.accentFrom} ${family.accentTo} hover:text-white text-xs font-semibold text-gray-700 transition-all duration-200`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{localizedFamily(family, language)}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const VariantCard = ({ variant, family, language, direction }) => {
  const { t } = useLanguage();
  const firstProduct = variant.products[0];
  const dir = direction;

  return (
    <article
      data-testid={`variant-card-${firstProduct.id}`}
      className={`group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-gray-300 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col ${
        dir === 'rtl' ? 'text-right' : ''
      }`}
    >
      {/* Image well — keeps mix-blend-mode multiply to drop white backgrounds */}
      <Link to={`/catalogue/${firstProduct.id}`} className="block relative">
        <div className={`relative h-44 ${family.bgTint} overflow-hidden`}>
          <img
            src={variant.image}
            alt={localizedName(variant, language)}
            className="absolute inset-0 w-full h-full object-contain p-4 mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {/* Variant-count chip if multiple sizes */}
          {variant.products.length > 1 && (
            <span
              className={`absolute top-2 ${dir === 'rtl' ? 'left-2' : 'right-2'} bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-gray-700 shadow-sm`}
            >
              {variant.products.length} {language === 'ar' ? 'أحجام' : language === 'en' ? 'sizes' : 'formats'}
            </span>
          )}
        </div>
      </Link>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className={`text-base font-bold text-gray-900 leading-snug line-clamp-2 ${family.textAccent} group-hover:opacity-80`}
        >
          <Link to={`/catalogue/${firstProduct.id}`}>{localizedName(variant, language)}</Link>
        </h3>

        {/* Sizes */}
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-1.5">
            {t('selection.availableSizes')}
          </p>
          <div className={`flex flex-wrap gap-1.5 ${dir === 'rtl' ? 'justify-end' : ''}`}>
            {variant.products.map((p) => (
              <Link
                key={p.id}
                to={`/catalogue/${p.id}`}
                data-testid={`size-pill-${p.id}`}
                className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border border-gray-200 hover:border-transparent hover:text-white hover:bg-gradient-to-r ${family.accentFrom} ${family.accentTo} transition-all`}
              >
                {p.size}
              </Link>
            ))}
          </div>
        </div>

        {/* View details */}
        <Link
          to={`/catalogue/${firstProduct.id}`}
          className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${family.textAccent} hover:gap-2 transition-all ${dir === 'rtl' ? 'flex-row-reverse self-end' : 'self-start'}`}
        >
          {t('selection.viewDetails')}
          {dir === 'rtl' ? (
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </Link>
      </div>
    </article>
  );
};

const FamilyBlock = ({ family, variants, language, direction }) => {
  const { t } = useLanguage();
  const Icon = family.icon;

  const totalSkus = variants.reduce((sum, v) => sum + v.products.length, 0);
  const variantCount = variants.length;

  const meta =
    variantCount === 1
      ? fmt(t('selection.familyMetaSingleVariant'), { skus: totalSkus })
      : fmt(t('selection.familyMeta'), { variants: variantCount, skus: totalSkus });

  return (
    <section
      id={`family-${family.slug}`}
      className="scroll-mt-32 py-10"
      data-testid={`family-block-${family.slug}`}
    >
      {/* Family header */}
      <header
        className={`flex items-center gap-4 mb-6 ${direction === 'rtl' ? 'flex-row-reverse text-right' : ''}`}
      >
        <div
          className={`shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${family.accentFrom} ${family.accentTo} flex items-center justify-center shadow-lg`}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
            {localizedFamily(family, language)}
          </h2>
          <p className={`text-sm font-semibold ${family.textAccent} mt-0.5`}>{meta}</p>
        </div>
      </header>

      {/* Variants grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {variants.map((v) => (
          <VariantCard
            key={v.key}
            variant={v}
            family={family}
            language={language}
            direction={direction}
          />
        ))}
      </div>
    </section>
  );
};

export const RetailSelectionPage = () => {
  const { language, direction, t } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const groups = useMemo(() => groupForSelection(productsData), []);

  const totals = useMemo(() => {
    let variants = 0;
    let skus = 0;
    groups.forEach((g) => {
      variants += g.variants.length;
      g.variants.forEach((v) => (skus += v.products.length));
    });
    return { families: groups.length, variants, skus };
  }, [groups]);

  return (
    <div dir={direction} className="bg-gradient-to-b from-white via-gray-50 to-white min-h-screen">
      {/* Hero */}
      <header
        className="relative overflow-hidden bg-blue-800 text-white"
        data-testid="selection-hero"
      >
        {/* Background photo — Olion supermarket aisle */}
        <img
          src="https://customer-assets.emergentagent.com/job_a5a52270-1821-48dd-806e-4fc805561245/artifacts/5q39bw9a_ChatGPT%20Image%20Feb%2016%2C%202026%2C%2002_15_08%20PM.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: direction === 'rtl' ? 'left center' : 'right center' }}
        />
        {/* Color overlay — solid blue where text sits, fading to transparent so the photo shows through over the products */}
        <div
          className={`absolute inset-0 ${
            direction === 'rtl'
              ? 'bg-gradient-to-l from-blue-900/95 via-blue-800/90 to-blue-700/10'
              : 'bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-700/10'
          }`}
        />
        {/* Warm orange wash on the far side for brand cohesion */}
        <div
          className={`absolute inset-0 ${
            direction === 'rtl'
              ? 'bg-gradient-to-tl from-transparent via-transparent to-orange-500/25'
              : 'bg-gradient-to-tr from-transparent via-transparent to-orange-500/25'
          } mix-blend-soft-light`}
        />
        {/* Subtle radial lights kept from before */}
        <div className="absolute inset-0 opacity-15 pointer-events-none [background-image:radial-gradient(circle_at_30%_20%,white,transparent_40%),radial-gradient(circle_at_80%_60%,white,transparent_45%)]" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
          <div
            className={`max-w-3xl ${direction === 'rtl' ? 'ml-auto text-right' : ''}`}
          >
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-5 border border-white/20">
              <Sparkles className="w-3.5 h-3.5" />
              {t('selection.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
              {t('selection.title')}
            </h1>
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
              {t('selection.subtitle')}
            </p>

            {/* Stats */}
            <div
              className={`mt-8 flex flex-wrap gap-3 ${direction === 'rtl' ? 'justify-end' : ''}`}
            >
              {[
                { value: totals.families, label: t('selection.stats.families') },
                { value: totals.variants, label: t('selection.stats.variants') },
                { value: totals.skus, label: t('selection.stats.skus') },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur border border-white/20 rounded-xl px-5 py-3"
                >
                  <div className="text-3xl font-bold leading-none">{s.value}</div>
                  <div className="text-xs uppercase tracking-wider mt-1 text-white/80">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div className={`mt-8 flex flex-wrap gap-3 ${direction === 'rtl' ? 'justify-end' : ''}`}>
              <DownloadCatalogButton
                language={language}
                variant="white"
                short
                testid="selection-download-pdf"
                className="px-5 py-3 text-sm"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Family anchor bar */}
      <FamilyAnchorBar groups={groups} direction={direction} />

      {/* Families */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 divide-y divide-gray-100">
        {groups.map(({ family, variants }) => (
          <FamilyBlock
            key={family.slug}
            family={family}
            variants={variants}
            language={language}
            direction={direction}
          />
        ))}
      </main>

      {/* Reseller CTA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div
          className={`bg-gradient-to-r from-blue-700 to-orange-500 rounded-3xl p-8 sm:p-12 text-white shadow-2xl flex flex-col md:flex-row items-center gap-6 ${
            direction === 'rtl' ? 'md:flex-row-reverse text-right' : ''
          }`}
        >
          <div className="flex-1">
            <h3 className="text-2xl sm:text-3xl font-bold mb-2">{t('selection.ctaTitle')}</h3>
            <p className="text-white/90 text-sm sm:text-base">{t('selection.ctaDesc')}</p>
          </div>
          <Link
            to="/#contact"
            data-testid="selection-cta-button"
            className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-gray-100 px-6 py-3.5 rounded-full font-bold text-sm shadow-lg transition-all whitespace-nowrap"
          >
            <Mail className="w-4 h-4" />
            {t('selection.ctaButton')}
          </Link>
        </div>
      </section>
    </div>
  );
};
